/* eslint-disable max-lines-per-function */
/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect } from 'react'
import { useLang } from '~/hooks/useLang'
import ErrorFallback from '~/components/ErrorFallback'
import { ErrorBoundary } from 'react-error-boundary'
import Layout from '~/components/Layout'
import {
  Button,
  Card,
  CardHeader,
  Collapse,
  Grid,
  IconButton,
  Tab,
} from '@mui/material'
import { DispatchSearchBox } from '~/components/DispatchSearchBox'
import { useHookstate, useState } from '@hookstate/core'
import {
  downloadReportCSV,
  makeRequestToGetQualifiersRecordByOp,
} from '../../../services/report'
import notify from '~/utils/notify'
import useReportState from '../../../stores/ReportState'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import GenerateReportButton from '../../../components/GenerateReportButton'
import ExportButtonCSV from '../../../components/ButtonExportCSV'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups'
import FilterAutocompleteAgencies from '~/components/FilterAutocompleteAgencies'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet'
import { type typesAndSubtypesSelectedProps } from '~/components/FilterAutocompleteTypesAndSubtypesWithDescription'
import { formatTypeAndSubtypeForReport } from '~/utils/format'

import FilterCheckboxQualifiers from '~/components/FilterCheckboxQualifiers'
import { generateCSVFileName } from '~/utils/strings'

type ListOccurrenceConsultationTab = 'simple' | 'detailed'

// eslint-disable-next-line max-lines-per-function
export default function QualifiersbyOptions() {
  const { reportData, loadingReport } = useReportState()
  const { translate } = useLang()

  const expandFilterSearch = useHookstate<boolean>(true)
  const isLoading = useState(false)
  const isSearchDialog = useState(false)
  const tab = useHookstate<ListOccurrenceConsultationTab>('simple')
  const IdDispatch = useHookstate<number | null>(null)
  const groupId = useHookstate<readonly number[]>([])

  const startDate = useHookstate<string | null>(null)
  const finishDate = useHookstate<string | null>(null)
  const agencyIds = useHookstate<readonly number[]>([])
  const labelButton = useHookstate('')
  const period = useHookstate<string>('')
  const lockFields = useHookstate<boolean>(false)
  const entryOriginId = useHookstate('')
  const groupSetId = useHookstate<number | null>(null)
  const typeId = useHookstate<number | null>(null)
  const subtypeId = useHookstate<number[] | null>(null)
  const qualifiersSelected = useHookstate<number[]>([])
  const clearState = useHookstate<boolean>(false)

  const typesAndSubtypesSelected = useHookstate<
    typesAndSubtypesSelectedProps[] | []
  >([])
  const typesFormatedForSending = useHookstate<number[]>([])
  const subTypesFormatedForSending = useHookstate<number[]>([])
  const operationId = useHookstate<number | null>(null)

  useEffect(() => {
    document.title = translate('Reports - Dispatch System')
  }, [])

  const handleTabDispatch = (
    event: React.SyntheticEvent,
    value: ListOccurrenceConsultationTab
  ) => {
    tab.set(value)
  }

  useEffect(() => {
    IdDispatch.set(null)
  }, [])

  const validateAndNotify = (condition: boolean, message: string) => {
    if (condition) {
      notify({
        message: translate(message),
        type: 'error',
      })
    }
  }

  async function generateReport(exportCSV = false) {
    if (
      (tab.get() === 'simple' &&
        IdDispatch.get() &&
        qualifiersSelected.get()) ||
      (tab.get() === 'detailed' && startDate.get() && finishDate.get())
    ) {
      formatTypeAndSubtypeForReport({
        typesAndSubtypesSelected: typesAndSubtypesSelected.get(),
        typesFormatedForSending: typesFormatedForSending,
        subTypesFormatedForSending: subTypesFormatedForSending,
      })
      loadingReport().set(true)
      isLoading.set(true)

      const data = await makeRequestToGetQualifiersRecordByOp({
        dispatch_id: tab.get() === 'simple' ? IdDispatch.get() : null,
        qualifier_options: qualifiersSelected.get() ?? [],
        dispatch_group_ids: tab.get() === 'detailed' ? groupId.get() : [],
        start_date: tab.get() === 'detailed' ? startDate.get() : null,
        finish_date: tab.get() === 'detailed' ? finishDate.get() : null,
        group_set_id: tab.get() === 'detailed' ? groupSetId.get() : null,
        agency_ids: tab.get() === 'detailed' ? agencyIds.get() : [],
        export_csv: exportCSV,
      }).finally(() => {
        loadingReport().set(false)
        isLoading.set(false)
        typesFormatedForSending.set([])
        subTypesFormatedForSending.set([])
      })

      if (!data || (Array.isArray(data) && data.length === 0)) {
        notify({
          message: translate('Não há dados suficientes para gerar o relatório'),
          type: 'error',
        })
        return
      }

      if (exportCSV) {
        downloadReportCSV(
          generateCSVFileName('Registration of qualifiers', translate),
          data
        )
      } else {
        reportData().set(data as any)
        window.open(`/report/result/report_qualifiers_by_options`)
      }
    } else {
      validateAndNotify(
        (!IdDispatch.get() || !qualifiersSelected.get()) &&
          tab.get() === 'simple',
        'Dispatch and qualified are mandatory in simple filter'
      )
      validateAndNotify(
        (!startDate.get() || !finishDate.get()) && tab.get() === 'detailed',
        'Start and end date are required in detailed filter'
      )
    }
  }

  const handleClearFilters = () => {
    IdDispatch.set(null)
    agencyIds.set([])
    groupSetId.set(null)
    groupId.set([])
    entryOriginId.set('')
    period.set('')
    typeId.set(null)
    subtypeId.set(null)
    typesAndSubtypesSelected.set([])
    typesFormatedForSending.set([])
    subTypesFormatedForSending.set([])
    operationId.set(null)
    clearState.set(!clearState.get())
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Layout marginLayout={true}>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <Grid container xs={12}>
            <Grid item>
              <CardHeader
                title={`${translate('Report')}: ${translate(
                  'Registration of qualifiers'
                )}`}
              />
            </Grid>
          </Grid>
        </Card>
        <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
          <CardHeader
            title={`${translate('Filters')}`}
            avatar={
              <IconButton
                onClick={() =>
                  expandFilterSearch.set(!expandFilterSearch.get())
                }
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  marginRight: 1,
                  padding: 0,
                }}
              >
                {expandFilterSearch.value ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            }
          />
          <Collapse in={expandFilterSearch.get()}>
            <Grid container direction={'column'} rowGap={2} padding={2}>
              <TabContext value={tab.get()}>
                <TabList onChange={handleTabDispatch}>
                  <Tab
                    label={translate('Simple filter')}
                    iconPosition="end"
                    value="simple"
                  />
                  <Tab
                    label={translate('Detailed filter')}
                    iconPosition="end"
                    value="detailed"
                  />
                </TabList>
                <TabPanel sx={{ padding: 0 }} value="simple">
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    alignItems="center"
                  >
                    <Grid item xs={3} sx={{ pl: 1, pt: 1 }}>
                      <DispatchSearchBox
                        isOpen={isSearchDialog}
                        isValid={true}
                        notOnlyVisibles={true}
                        isReport={true}
                        size="small"
                        dispatchId={IdDispatch}
                      />
                    </Grid>
                    <Grid item xs={3} sx={{ pt: 1, pl: 1 }}>
                      <FilterCheckboxQualifiers
                        qualifiersSelected={qualifiersSelected}
                        clearState={clearState?.get()}
                      />
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel sx={{ padding: 0 }} value="detailed">
                  <Grid container direction={'row'} rowGap={2} xs={12}>
                    <Grid item xs={2} sx={{ pt: 1, pl: 1 }}>
                      <FilterCheckboxQualifiers
                        qualifiersSelected={qualifiersSelected}
                        clearState={clearState?.get()}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteAgencies
                        disabled={lockFields.get()}
                        agenciesId={agencyIds.get()}
                        onAgenciesChange={(agency) => {
                          labelButton.set(translate('Search'))
                          agencyIds.set(agency)
                        }}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteDispatchGroupSet
                        disabled={lockFields.get()}
                        dispatchGroupSetId={groupSetId?.get() ?? undefined}
                        onGroupDispatchSetChange={(group) => {
                          groupSetId?.set(group)
                        }}
                      />
                    </Grid>
                    <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
                      <FilterAutocompleteDispatchsGroups
                        disabled={lockFields.get()}
                        agencyId={agencyIds.get() ?? undefined}
                        dispatcheGroups={groupId.get() ?? undefined}
                        onGroupDispatchChange={(group) => {
                          groupId.set(group)
                          labelButton.set(translate('Search'))
                        }}
                        groupSetId={groupSetId.get()}
                      />
                    </Grid>
                    <FilterSelectPeriod
                      disabled={lockFields.get()}
                      onStartDateChange={(value) =>
                        startDate.set(value?.toJSON() ?? null)
                      }
                      onFinishDateChange={(value) =>
                        finishDate.set(value?.toJSON() ?? null)
                      }
                      onPeriodChange={(value) => {
                        period.set(value)
                      }}
                      period={period.get()}
                      required
                    />
                  </Grid>
                </TabPanel>
              </TabContext>
            </Grid>
            <Grid
              container
              xs={12}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 2,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => handleClearFilters()}
                  sx={{ height: '40px' }}
                >
                  {translate('Clear Filters')}
                </Button>
                <ExportButtonCSV
                  isLoading={isLoading.get()}
                  dataToExport={() => generateReport(true)}
                />
                <GenerateReportButton
                  isLoading={isLoading.get()}
                  makeReport={generateReport}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Card>
      </Layout>
    </ErrorBoundary>
  )
}
